/**
 * BankAccountsPanel — Tab „Konten & Importe" (architecture-bank-reconciliation.md
 * §10.1). Konten-CRUD (Archiv statt Delete, §7.1) + Import-Historie
 * (`bank_statement_imports`, §6.3).
 *
 * @module BankAccountsPanel
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconBank, IconBilling, IconArchive, IconBoxOpen, IconEdit, IconFileImport } from '../../icons';
import { ClipLoader } from 'react-spinners';
import {
  listBankAccounts,
  archiveBankAccount,
  updateBankAccount,
  listStatementImports,
} from '../../api/bankApi';
import type { BankAccount, BankAccountType, BankStatementImport } from '../../types/bankTypes';
import { centsToEuros } from '../../utils/moneyUtils';
import { formatDate as formatDateCentral } from '../../utils/formatters';
import logger from '../../utils/logger';
import { mapErrorToUserMessage } from '../../utils/userErrorMessage';

const editInputClass =
  'w-full bg-surface border border-hairline rounded-lg px-2.5 py-1.5 text-sm text-primary focus:outline-none focus:border-indigo-500 disabled:opacity-50';

export interface BankAccountsPanelProps {
  /** Bump to force a reload (e.g. after a successful import). */
  refreshToken: number;
  onOpenImportWizard: () => void;
}

const accountTypeIcon = (type: BankAccountType) =>
  type === 'kreditkarte' ? <IconBilling className="text-accent-ink" /> : <IconBank className="text-accent-ink" />;

const BankAccountsPanel: React.FC<BankAccountsPanelProps> = ({ refreshToken, onOpenImportWizard }) => {
  const { t, i18n } = useTranslation('bank');

  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [imports, setImports] = useState<BankStatementImport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editIban, setEditIban] = useState('');
  const [editBic, setEditBic] = useState('');
  const [editBankName, setEditBankName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([listBankAccounts(), listStatementImports({ limitCount: 30 })])
      .then(([accs, imps]) => {
        setAccounts(accs);
        setImports(imps);
      })
      .catch((err: any) => {
        logger.error(err, 'bank accounts load failed');
        setError(mapErrorToUserMessage(err, t, { fallbackKey: 'accounts.loadFailed' }));
      })
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  const handleToggleArchive = async (account: BankAccount) => {
    try {
      await archiveBankAccount(account.id, !account.isArchived);
      load();
    } catch (err: any) {
      logger.error(err, 'bank account archive toggle failed');
      setError(mapErrorToUserMessage(err, t, { fallbackKey: 'accounts.archiveFailed' }));
    }
  };

  /**
   * MA-2 (architecture-mahn-automatik.md §6.4): toggles whether this account
   * is considered by the Mahn-Automatik Coverage-Gate. `dunningRelevant`
   * missing on the doc reads as `true` (fail-closed-Richtung, §11) — the
   * checkbox therefore defaults to checked for accounts without the field.
   */
  const handleToggleDunningRelevant = async (account: BankAccount) => {
    const nextValue = account.dunningRelevant === false;
    try {
      await updateBankAccount(account.id, { dunningRelevant: nextValue });
      load();
    } catch (err: any) {
      logger.error(err, 'bank account dunningRelevant toggle failed');
      setError(mapErrorToUserMessage(err, t, { fallbackKey: 'accounts.updateFailed' }));
    }
  };

  const startEdit = (account: BankAccount) => {
    setEditingId(account.id);
    setEditName(account.displayName);
    setEditIban(account.iban ?? '');
    setEditBic(account.bic ?? '');
    setEditBankName(account.bankName ?? '');
    setError(null);
  };

  const saveEdit = async (accountId: string) => {
    setSavingEdit(true);
    try {
      // Leere Felder → null (NIE undefined — Firestore lehnt undefined ab). IBAN
      // wird in updateBankAccount() zusätzlich normalisiert (uppercase, no
      // spaces). BR-18-Korrektur (§5.2/§6.1): IBAN ist für ALLE Kontotypen
      // optional — Kreditkarten-Konten werden NICHT mehr hart auf null gezwungen.
      await updateBankAccount(accountId, {
        displayName: editName,
        iban: editIban.trim() || null,
        bic: editBic.trim() || null,
        bankName: editBankName.trim() || null,
      });
      setEditingId(null);
      load();
    } catch (err: any) {
      logger.error(err, 'bank account update failed');
      setError(mapErrorToUserMessage(err, t, { fallbackKey: 'accounts.updateFailed' }));
    } finally {
      setSavingEdit(false);
    }
  };

  const visibleAccounts = accounts.filter((a) => showArchived || !a.isArchived);

  const formatDate = (iso: string | null) => formatDateCentral(iso, { locale: i18n.language });

  const importStatusBadge = (imp: BankStatementImport) =>
    imp.status === 'partial' ? (
      <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--tone-warning-bg)] text-[var(--tone-warning-fg)] border border-[var(--tone-warning-border)]">
        {t('accounts.importStatusPartial', 'teilweise')}
      </span>
    ) : (
      <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)] border border-[var(--tone-success-border)]">
        {t('accounts.importStatusCompleted', 'vollständig')}
      </span>
    );

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-primary font-semibold">{t('accounts.title', 'Konten')}</h2>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-muted cursor-pointer">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              {t('accounts.showArchived', 'Archivierte anzeigen')}
            </label>
            <button
              type="button"
              onClick={onOpenImportWizard}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg"
            >
              <IconFileImport />
              {t('accounts.newImport', 'Neuer Import')}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg p-3 text-sm text-[var(--tone-danger-fg)] mb-4">{error}</div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <ClipLoader color="#6366f1" size={28} />
          </div>
        ) : visibleAccounts.length === 0 ? (
          <div className="bg-surface-raised border border-hairline rounded-xl p-8 text-center text-muted">
            {t('accounts.empty', 'Noch keine Konten angelegt — starte einen Import.')}
          </div>
        ) : (
          <div className="space-y-2">
            {visibleAccounts.map((account) =>
              editingId === account.id ? (
                <div
                  key={account.id}
                  className="bg-surface-raised border border-indigo-700 rounded-lg px-4 py-3 space-y-3"
                >
                  <div className="flex items-center gap-2 text-primary text-sm font-medium">
                    {accountTypeIcon(account.accountType)}
                    {t('accounts.editTitle', 'Konto bearbeiten')}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted mb-1">
                      {t('wizard.account.field.displayName', 'Bezeichnung *')}
                    </label>
                    <input
                      className={editInputClass}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-muted mb-1">
                        {t('wizard.account.field.iban', 'IBAN')}
                      </label>
                      <input
                        className={editInputClass}
                        value={editIban}
                        placeholder="DE…"
                        onChange={(e) => setEditIban(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-muted mb-1">
                        {t('wizard.account.field.bic', 'BIC')}
                      </label>
                      <input
                        className={editInputClass}
                        value={editBic}
                        onChange={(e) => setEditBic(e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted mb-1">
                      {t('wizard.account.field.bankName', 'Bank')}
                    </label>
                    <input
                      className={editInputClass}
                      value={editBankName}
                      onChange={(e) => setEditBankName(e.target.value)}
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="px-3 py-1.5 text-sm text-muted hover:text-primary"
                    >
                      {t('wizard.cancel', 'Abbrechen')}
                    </button>
                    <button
                      type="button"
                      disabled={savingEdit || !editName.trim()}
                      onClick={() => saveEdit(account.id)}
                      className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg disabled:opacity-50"
                    >
                      {t('accounts.save', 'Speichern')}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  key={account.id}
                  className={`flex items-center justify-between bg-surface-raised border border-hairline rounded-lg px-4 py-3 ${
                    account.isArchived ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {accountTypeIcon(account.accountType)}
                    <div className="min-w-0">
                      <p className="text-primary text-sm font-medium truncate">{account.displayName}</p>
                      <p className="text-xs text-faint truncate">
                        {account.iban ?? t('wizard.account.noIban', 'ohne IBAN')}
                        {account.lastKnownBalanceCents !== null &&
                          ` · ${centsToEuros(account.lastKnownBalanceCents).toLocaleString(i18n.language || 'de-DE', {
                            style: 'currency',
                            currency: 'EUR',
                          })} (${formatDate(account.lastKnownBalanceDate)})`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <label
                      className="flex items-center gap-1.5 text-xs text-muted cursor-pointer"
                      title={t(
                        'accounts.dunningRelevantHint',
                        'Wird dieses Konto bei der Mahn-Automatik als abgedeckt geprüft? (Kundenzahlungen können hier eingehen)'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={account.dunningRelevant !== false}
                        onChange={() => handleToggleDunningRelevant(account)}
                      />
                      {t('accounts.dunningRelevant', 'Mahn-Automatik')}
                    </label>
                    <button
                      type="button"
                      onClick={() => startEdit(account)}
                      title={t('accounts.edit', 'Bearbeiten')}
                      className="text-faint hover:text-primary"
                    >
                      <IconEdit size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleArchive(account)}
                      title={account.isArchived ? t('accounts.unarchive', 'Reaktivieren') : t('accounts.archive', 'Archivieren')}
                      className="text-faint hover:text-primary"
                    >
                      {account.isArchived ? <IconBoxOpen size={14} /> : <IconArchive size={14} />}
                    </button>
                  </div>
                </div>
              ),
            )}
          </div>
        )}
      </div>

      <div>
        <h2 className="text-primary font-semibold mb-4">{t('accounts.importHistory', 'Import-Historie')}</h2>
        {imports.length === 0 ? (
          <p className="text-sm text-faint">{t('accounts.noImports', 'Noch keine Importe.')}</p>
        ) : (
          <div className="border border-hairline rounded-lg overflow-hidden overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-surface-raised text-muted">
                <tr>
                  <th className="text-left px-3 py-2">{t('accounts.col.filename', 'Datei')}</th>
                  <th className="text-left px-3 py-2">{t('accounts.col.period', 'Zeitraum')}</th>
                  <th className="text-right px-3 py-2">{t('accounts.col.imported', 'Importiert')}</th>
                  <th className="text-right px-3 py-2">{t('accounts.col.duplicates', 'Duplikate')}</th>
                  <th className="text-right px-3 py-2">{t('accounts.col.errors', 'Fehler')}</th>
                  <th className="text-left px-3 py-2">{t('accounts.col.status', 'Status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--border-hairline)]">
                {imports.map((imp) => (
                  <tr key={imp.id} className="text-secondary">
                    <td className="px-3 py-2 max-w-[200px] truncate">{imp.filename}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {imp.statementFrom ? `${formatDate(imp.statementFrom)} – ${formatDate(imp.statementTo)}` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">{imp.stats.imported}</td>
                    <td className="px-3 py-2 text-right">{imp.stats.duplicates}</td>
                    <td className="px-3 py-2 text-right">{imp.stats.errors}</td>
                    <td className="px-3 py-2">{importStatusBadge(imp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BankAccountsPanel;
